import { useNuxtApp } from '#app'
import { computed } from 'vue'
import jsonActivites from '~/assets/activities.json'
import type { ActivityData } from '~/types/activity'
import type { Mission, MissionAddress, MissionTag } from '~/types/mission'

export function useMission(mission: Mission) {
  const { $filters, $dayjs } = useNuxtApp()

  const hasExpired = computed(() => {
    const now = $dayjs()
    const endDate = mission.end_date
    if (!endDate) {
      return false
    }

    const endDateObject =
      typeof endDate === 'number' && $dayjs.unix(endDate).isValid()
        ? $dayjs.unix(endDate)
        : $dayjs(endDate, 'YYYY-MM-DD HH:mm:ss', 'fr', true).isValid()
        ? $dayjs(endDate, 'YYYY-MM-DD HH:mm:ss')
        : $dayjs(endDate).isValid()
        ? $dayjs(endDate)
        : null

    if (!endDateObject) {
      return false
    }

    return mission.provider == 'api_engagement'
      ? endDateObject.isBefore(now)
      : endDateObject.endOf('day').isBefore(now)
  })

  const firstAddress = computed<MissionAddress | null>(() => {
    if (mission.type !== 'Mission en présentiel') {
      return null
    }
    if (!mission.addresses || mission.addresses.length === 0) {
      return null
    }
    return mission.addresses[0]
  })

  const formattedDates = computed(() => {
    const startDate = mission.start_date
    const endDate = mission.end_date
    if (!startDate) {
      return null
    }
    if (startDate && endDate) {
      if ($dayjs(startDate).isSame($dayjs(endDate))) {
        return `Le ${$dayjs(startDate).format('D MMMM YYYY')}`
      }
      return `Du ${$dayjs(startDate).format('D MMMM YYYY')} au ${$dayjs(endDate).format(
        'D MMMM YYYY',
      )}`
    }

    return `À partir du ${$dayjs(startDate).format('D MMMM YYYY')}`
  })

  const formattedCommitment = computed(() => {
    if (mission.commitment__time_period) {
      return `${$filters.label(mission.commitment__duration, 'duration')} par ${$filters.label(
        mission.commitment__time_period,
        'time_period',
      )}`
    }
    return mission.commitment__duration
      ? $filters.label(mission.commitment__duration, 'duration')
      : null
  })

  const thumbnail = computed(() => {
    if (mission.provider === 'api_engagement') {
      return thumbnailApi.value
    }
    return (
      mission.template?.picture?.small ??
      '/images/card-thumbnail-default.jpg, /images/card-thumbnail-default@2x.jpg 2x'
    )
  })

  const thumbnailApi = computed(() => {
    let filename
    switch (mission.domaine?.id) {
      case 1:
        filename = 'api__sante_pour_tous'
        break
      case 2:
        filename = 'api__prevention_protection'
        break
      case 3:
        filename = 'api__art_culture'
        break
      case 4:
        filename = 'api__sport_pour_tous'
        break
      case 5:
        filename = 'api__covid19'
        break
      case 6:
        filename = 'api__cooperation_internationale'
        break
      case 7:
        filename = 'api__solidarite_insertion'
        break
      case 8:
        filename = 'api__memoire_citoyennete'
        break
      case 9:
        filename = 'api__education_pour_tous'
        break
      case 10:
        filename = 'api__protection_nature'
        break

      default:
        return '/images/card-thumbnail-default.jpg, /images/card-thumbnail-default@2x.jpg 2x'
    }

    const filepath = `/images/missions/api_engagement/${filename}`
    return `${filepath}.webp, ${filepath}@2x.webp 2x, ${filepath}.jpg, ${filepath}@2x.jpg 2x`
  })

  const activity = computed<ActivityData | null>(() => {
    const activity = mission?.activity ?? mission?.template?.activity
    return jsonActivites.find((activityJson) => activityJson.id === activity?.id) ?? null
  })

  const activitySecondary = computed<ActivityData | null>(() => {
    const activity = mission?.activity_secondary ?? mission?.template?.activity_secondary
    return jsonActivites.find((activityJson) => activityJson.id === activity?.id) ?? null
  })

  const activities = computed(() => {
    return [activity.value, activitySecondary.value].filter((a): a is ActivityData => Boolean(a))
  })

  const isStateDraft = computed(() => {
    return mission.state === 'Brouillon'
  })

  const isStateValidated = computed(() => {
    return mission.state === 'Validée'
  })

  function hasTag(tag: string) {
    if (!mission.tags) {
      return false
    }

    return (mission.tags ?? []).some((t) => {
      if (typeof t === 'string') {
        return t === tag
      }
      if (typeof t === 'object' && t !== null && 'name' in t) {
        return (t as { name: string }).name === tag
      }
    })
  }

  return {
    activity,
    activitySecondary,
    activities,
    thumbnail,
    thumbnailApi,
    firstAddress,
    formattedDates,
    formattedCommitment,
    hasExpired,
    isStateDraft,
    isStateValidated,
    hasTag,
  }
}
